import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash, X } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const kindLabels = {
  new_user: "Üyelik",
  subscription: "Abonelik",
  support: "Destek",
  support_reply: "Destek",
  plan: "Plan",
  packing_list: "Packing List",
  invoice: "Commercial Invoice",
  warehouse: "Warehouse",
  document_mail: "Belge E-mail",
  team: "Ekip",
};

const relativeTime = (value) => {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const diff = Math.max(0, Date.now() - dt.getTime());
  const minute = 60 * 1000, hour = 60 * minute, day = 24 * hour;
  if (diff < minute) return "Şimdi";
  if (diff < hour) return `${Math.floor(diff / minute)} dk önce`;
  if (diff < day) return `${Math.floor(diff / hour)} sa önce`;
  if (diff < 7 * day) return `${Math.floor(diff / day)} gün önce`;
  return dt.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function NotificationCenter() {
  const nav = useNavigate();
  const boxRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/notifications");
      setRows(r.data?.notifications || []);
      setUnread(Number(r.data?.unread || 0));
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail) || "Bildirimler yüklenemedi");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const timer = window.setInterval(() => load(true), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    load();
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open, load]);

  const markRead = async (row, follow = false) => {
    try {
      if (!row.read) await api.post(`/notifications/${encodeURIComponent(row.id)}/read`);
      setRows((prev) => prev.map((x) => x.id === row.id ? { ...x, read: true } : x));
      setUnread((n) => Math.max(0, n - (row.read ? 0 : 1)));
      if (follow && row.url) { setOpen(false); nav(row.url); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bildirim güncellenemedi"); }
  };

  const remove = async (row, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/notifications/${encodeURIComponent(row.id)}`);
      setRows((prev) => prev.filter((x) => x.id !== row.id));
      if (!row.read) setUnread((n) => Math.max(0, n - 1));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bildirim silinemedi"); }
  };

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all", { ids: rows.map((x) => x.id) });
      setRows((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bildirimler güncellenemedi"); }
  };

  const deleteAll = async () => {
    if (!rows.length) return;
    if (!window.confirm("Tüm bildirimler silinsin mi?")) return;
    try {
      await api.post("/notifications/delete-all", { ids: rows.map((x) => x.id) });
      setRows([]); setUnread(0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bildirimler silinemedi"); }
  };

  return <div ref={boxRef} className="relative" data-testid="notification-center">
    <button type="button" onClick={() => setOpen((v) => !v)} className="relative inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 text-slate-700 font-semibold shadow-sm hover:border-blue-500 hover:text-blue-700" data-testid="notification-toggle">
      <Bell size={19} weight={unread ? "fill" : "duotone"}/><span className="hidden xl:inline">Bildirimler</span>
      {unread > 0 && <span className="absolute -right-2 -top-2 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[10px] leading-5 text-center font-bold">{unread > 99 ? "99+" : unread}</span>}
    </button>
    {open && <div className="absolute right-0 mt-2 w-[min(94vw,430px)] bg-white border border-slate-200 shadow-2xl z-[160]" data-testid="notification-popup">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b bg-slate-50">
        <div><div className="font-bold text-slate-900">Bildirimler</div><div className="text-xs text-slate-500">{unread} okunmamış · {rows.length} toplam</div></div>
        <button onClick={() => setOpen(false)} className="p-1 text-slate-500 hover:text-slate-900"><X size={18}/></button>
      </div>
      <div className="max-h-[520px] overflow-y-auto">
        {loading && !rows.length && <div className="p-8 text-center text-sm text-slate-400">Bildirimler yükleniyor…</div>}
        {!loading && !rows.length && <div className="p-8 text-center"><Bell size={30} className="mx-auto text-slate-300"/><div className="mt-2 text-sm text-slate-500">Yeni bildiriminiz yok.</div></div>}
        {rows.map((row) => <button key={row.id} onClick={() => markRead(row, true)} className={`w-full text-left px-4 py-3 border-b hover:bg-slate-50 flex gap-3 ${row.read ? "bg-white" : "bg-blue-50/60"}`}>
          <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${row.read ? "bg-slate-200" : row.severity === "warning" ? "bg-amber-500" : row.severity === "success" ? "bg-emerald-500" : "bg-blue-600"}`}/>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-sm text-slate-900">{row.title}</span><span className="text-[9px] uppercase tracking-wider border px-1.5 py-0.5 text-slate-500">{kindLabels[row.kind] || row.kind}</span></span>
            <span className="block text-sm text-slate-600 mt-1">{row.detail}</span>
            {(row.company || row.actor) && <span className="block text-[11px] text-slate-400 mt-1">{row.company ? `${row.company}${row.actor ? " · " : ""}` : ""}{row.actor || ""}</span>}
            <span className="block text-[10px] text-slate-400 mt-1">{relativeTime(row.created_at)}</span>
          </span>
          <span className="flex flex-col gap-1 shrink-0">
            {!row.read && <span onClick={(e) => { e.stopPropagation(); markRead(row); }} title="Okundu yap" className="p-1.5 border hover:bg-white"><Check size={14}/></span>}
            <span onClick={(e) => remove(row, e)} title="Sil" className="p-1.5 border text-red-600 hover:bg-red-50"><Trash size={14}/></span>
          </span>
        </button>)}
      </div>
      <div className="grid grid-cols-2 gap-2 p-3 border-t bg-slate-50">
        <button onClick={readAll} disabled={!rows.length || unread === 0} className="px-3 py-2 border bg-white text-xs font-semibold disabled:opacity-40">Tümünü Okundu Yap</button>
        <button onClick={deleteAll} disabled={!rows.length} className="px-3 py-2 border border-red-200 bg-white text-red-600 text-xs font-semibold disabled:opacity-40">Tümünü Sil</button>
      </div>
    </div>}
  </div>;
}
